/**
 * Enemies (GDD §5, §5.1, §5.2, §3.7; concept L976–L982, L1013–L1059): sleepers wake, patrols turn at the banks,
 * enemy jets cross, every enemy turns smoothly to its heading, bobs and animates; helicopters and gunboats fire
 * once their level allows; the dead spin and fall, or list and sink.
 */
import { config } from '../core/config.ts';
import { fract } from '../core/math.ts';
import type { GameSim } from '../sim/context.ts';
import { listRemoveAt, type Enemy } from '../sim/state.ts';
import { addScore, points } from '../rules/scoring.ts';
import { releaseEnemy } from './pool.ts';
import { enemyFire } from './shots.ts';

const HALF_PI = Math.PI / 2;
/** Enemies more than 70 m behind the jet are gone (concept L1016). */
const BEHIND = 70;
/** Turn smoothing (concept L1038). */
const TURN_RATE = 3.5;
/** Death: helicopters 3 s, enemy jets at once, boats 3.2 s; a helicopter first lifts at 3 m/s (concept L978). */
const DYING_HELI = 3;
const DYING_JET = 0.01;
const DYING_BOAT = 3.2;
/** Falling helicopter: gravity, spin, roll, splash height (L1019). */
const HELI_FALL_G = 16;
const HELI_SPIN = 7;
const HELI_ROLL = 1.2;
const HELI_SPLASH_Y = 0.6;
/** Sinking: barges 1.0 m/s, ships 1.5 m/s, listing 0.22 rad/s (L1021). */
const SINK_FUEL = 1.0;
const SINK_SHIP = 1.5;
const LIST_RATE = 0.22;
/** Rotor speeds (rad/s, L1043). */
const ROTOR = 26;
const TAIL = 38;

/** Explosion sizes (concept L979). */
function blastSize(e: Enemy): number {
  return e.type === 'fuel' ? 1.6 : e.type === 'jet' ? 1.2 : 0.9;
}

/** concept killEnt (L976–L982). Collisions score nothing (GDD §4.7). */
export function killEnemy(sim: GameSim, e: Enemy, noScore = false): void {
  if (e.dying > 0) return;
  e.dying = e.type === 'heli' ? DYING_HELI : e.type === 'jet' ? DYING_JET : DYING_BOAT;
  e.vy = 3;
  sim.fx.boom(e.x, e.y + (e.type === 'fuel' ? 3 : 0), e.z, blastSize(e));
  if (!noScore) addScore(sim, points(e.type));
  e.wake = false;
}

function removeAt(sim: GameSim, i: number, e: Enemy): void {
  releaseEnemy(sim.pool, e);
  listRemoveAt(sim.ents, i);
}

/** concept updEnts (L1013–L1059), last spawned first. */
export function stepEnemies(sim: GameSim, dt: number, t: number): void {
  const run = sim.run;
  const jet = sim.jet;
  const W = sim.W;
  const ents = sim.ents;
  const f = config.firing;
  const en = config.enemies;
  const quiet = sim.rules === 'gdd' && run.invuln > 0; // D7: no enemy fires while the jet is invulnerable
  for (let i = ents.count - 1; i >= 0; i--) {
    const e = ents.items[i];
    if (!e) continue;
    e.prevX = e.posX;
    e.prevY = e.posY;
    e.prevZ = e.posZ;
    if (e.z > jet.z + BEHIND || (e.type === 'jet' && Math.abs(e.x - W.center(e.z)) > W.width(e.z) + en.jetExitBeyond)) {
      removeAt(sim, i, e);
      continue;
    }
    if (e.dying > 0) {
      e.dying -= dt;
      if (e.type === 'heli') {
        e.vy -= HELI_FALL_G * dt;
        e.y += e.vy * dt;
        e.posY = e.y;
        e.rotY += dt * HELI_SPIN;
        e.rotZ += dt * HELI_ROLL;
        if (e.y < HELI_SPLASH_Y) {
          sim.fx.splash(e.x, e.z, 1.2);
          e.dying = 0;
        }
      } else if (e.type !== 'jet') {
        e.posY -= dt * (e.type === 'fuel' ? SINK_FUEL : SINK_SHIP);
        e.rotZ += dt * LIST_RATE * e.face;
        sim.fx.sinkSmoke(e.x, e.z, dt);
      }
      if (e.dying <= 0) removeAt(sim, i, e);
      continue;
    }
    const c = W.center(e.z);
    const w = W.width(e.z);
    const ahead = jet.z - e.z;
    if (e.near && !e.move && ahead < en.sleeperRange) e.move = true;
    if (e.move && (e.type === 'ship' || e.type === 'heli')) {
      if (!e.vx) e.vx = e.face * e.spd;
      e.x += e.vx * dt;
      const lim = Math.max(w - (e.type === 'heli' ? en.bankClearance.heli : e.hw + en.bankClearance.gunboatExtra), 3);
      if (e.x > c + lim) {
        e.x = c + lim;
        e.vx = -Math.abs(e.vx);
      } else if (e.x < c - lim) {
        e.x = c - lim;
        e.vx = Math.abs(e.vx);
      }
      e.face = e.vx > 0 ? 1 : -1;
    }
    if (e.type === 'jet') e.x += e.vx * dt;
    const ty = e.type === 'fuel' ? 0 : e.face > 0 ? -HALF_PI : HALF_PI;
    let dy = ty - e.yaw;
    dy = Math.atan2(Math.sin(dy), Math.cos(dy));
    e.yaw += dy * (1 - Math.exp(-dt * TURN_RATE));
    const bob =
      e.type === 'heli' ? 0.8 * Math.sin(t * 1.1 + e.ph) : e.type === 'jet' ? 0 : 0.1 * Math.sin(t * 1.5 + e.ph);
    e.posX = e.x;
    e.posY = e.y + bob;
    e.posZ = e.z;
    if (e.type === 'heli') {
      e.rotX = e.move ? -0.14 : 0.04;
      e.rotY = e.yaw;
      e.rotZ = 0;
      sim.pool.rotor[e.slot] = (sim.pool.rotor[e.slot] ?? 0) + dt * ROTOR;
      sim.pool.tail[e.slot] = (sim.pool.tail[e.slot] ?? 0) + dt * TAIL;
      e.light = fract(t * 1.2 + e.ph) < 0.15;
    } else if (e.type === 'ship') {
      e.rotX = 0.01 * Math.sin(t * 1.3 + e.ph);
      e.rotY = e.yaw;
      e.rotZ = 0.02 * Math.sin(t * 1.1 + e.ph);
      e.radar = t * 2.4;
      e.wake = e.move;
    } else if (e.type === 'fuel') {
      e.rotX = 0;
      e.rotY = e.yaw;
      e.rotZ = 0.012 * Math.sin(t * 1.1 + e.ph);
      e.light = fract(t * 0.8 + e.ph) < 0.5;
    } else {
      e.rotX = 0;
      e.rotY = e.yaw;
      e.rotZ = -e.face * 0.2;
      e.light = fract(t * 1.3) < 0.1;
    }
    if (!e.posed) {
      // First pose: draw from here, not from the origin.
      e.posed = true;
      e.prevX = e.posX;
      e.prevY = e.posY;
      e.prevZ = e.posZ;
    }
    if (!e.fire || !run.alive || run.title > 0 || quiet) continue;
    const ship = e.type === 'ship';
    const shooter = ship ? f.gunboat : f.heli;
    if (ahead > shooter.range.min && ahead < shooter.range.max && Math.abs(jet.x - e.x) < (shooter.side ?? 0)) {
      e.cd -= dt;
      if (e.cd <= 0) {
        enemyFire(sim, e.x, e.y + (ship ? 3 : -0.5), e.z, ship ? 'flak' : 'tracer', ship ? 'flak' : 'heliFire');
        const cd = f.cooldown;
        e.cd = Math.max(cd.min, cd.base - run.level * cd.perLevel) + sim.streams.combat() * cd.jitter;
      }
    }
  }
}

/** Every enemy is released in spawn order, as the concept's newGame does (L1135), so slots are reused alike. */
export function clearEnemies(sim: GameSim): void {
  for (let i = 0; i < sim.ents.count; i++) {
    const e = sim.ents.items[i];
    if (e) releaseEnemy(sim.pool, e);
  }
  sim.ents.count = 0;
}

/** concept respawn (L1130, D9 under concept rules): enemies from 40 m behind to 210 m ahead of the jet go. */
export function clearAroundJet(sim: GameSim): void {
  const z = sim.jet.z;
  for (let i = sim.ents.count - 1; i >= 0; i--) {
    const e = sim.ents.items[i];
    if (!e) continue;
    const d = z - e.z;
    if (d > -40 && d < 210) removeAt(sim, i, e);
  }
}
